/**
 * PaymentPlanModal — spawn a scheduled payment plan on an overdue invoice.
 *
 * Owners pick: amount per instalment, cadence (every N days), count, start.
 * Backend creates the plan + a `pending_agreement` schedule. If the user has
 * reminders=mailto, we hand the agreement email draft to the parent (which
 * pops the MailtoAttachmentModal). Otherwise the system path emails the
 * client an "Accept this plan" link.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X, Loader2, CheckCircle2, Calendar, DollarSign, Repeat, FileText,
  AlertCircle, Mail,
} from "lucide-react";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";

export default function PaymentPlanModal({ invoice, onClose, onCreated }) {
  const alreadyPaid = Number(invoice.paid_amount || 0);
  const remaining = Math.max(0, Number(invoice.total || 0) - alreadyPaid);

  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    amount_per_installment: Math.ceil(remaining / 3).toFixed(2),
    interval_days: 14,
    installments_total: 3,
    first_due_date: new Date().toISOString().slice(0, 10),
    send_agreement_email: !!invoice.bill_to_email,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/invoices/${invoice.id}/payment-plan`);
        if (r.data) setExisting(r.data);
      } catch { /* 404 means no plan yet, keep going */ }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [invoice.id]);

  // Live schedule preview
  const schedule = useMemo(() => {
    const amt = Number(form.amount_per_installment) || 0;
    const n = Math.max(1, Math.min(24, Number(form.installments_total) || 1));
    const i = Math.max(1, Math.min(90, Number(form.interval_days) || 1));
    const start = form.first_due_date ? new Date(form.first_due_date) : new Date();
    const rows = [];
    for (let k = 0; k < n; k += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i * k);
      rows.push({ due: d.toISOString().slice(0, 10), amount: amt });
    }
    return rows;
  }, [form]);

  const scheduledTotal = schedule.reduce((s, r) => s + r.amount, 0);
  const diff = scheduledTotal - remaining;
  const overByTooMuch = diff > remaining * 0.5 + 1;
  const underByABit = diff < -0.5;

  const submit = async (e) => {
    e.preventDefault();
    if (Number(form.amount_per_installment) <= 0) { toast.error("Amount must be > 0"); return; }
    if (overByTooMuch) { toast.error("Plan total is too far above remaining — lower the amount or count"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/invoices/${invoice.id}/payment-plan`, {
        amount_per_installment: Number(form.amount_per_installment),
        interval_days: Number(form.interval_days),
        installments_total: Number(form.installments_total),
        first_due_date: form.first_due_date,
        send_agreement_email: form.send_agreement_email,
      });
      toast.success("Payment plan created");
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const cancelExisting = async () => {
    if (!window.confirm("Cancel this active plan? The client will no longer receive scheduled reminders.")) return;
    try {
      await api.post(`/payment-plans/${existing.id}/cancel`);
      toast.success("Plan cancelled");
      onCreated({ cancelled: true });
    } catch { toast.error("Cancel failed"); }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[80] bg-black/50 grid place-items-center" onClick={onClose}>
        <Loader2 className="animate-spin text-white" size={20} />
      </div>
    );
  }

  // Show existing plan status (owner can cancel)
  if (existing && ["pending_agreement", "active"].includes(existing.status)) {
    const paidCount = (existing.schedule || []).filter((s) => s.status === "paid").length;
    const missedCount = (existing.schedule || []).filter((s) => s.status === "missed").length;
    return (
      <div className="fixed inset-0 z-[80] bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid="payment-plan-existing">
        <div onClick={(e) => e.stopPropagation()} className="bg-white border w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-xl" style={{ borderColor: "var(--ll-ink)" }}>
          <div className="px-5 py-3 border-b bg-[#0b5d9e] text-white flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-75">Payment plan</div>
              <div className="font-display font-bold text-sm">Invoice {invoice.invoice_number} · {existing.status.replace("_", " ")}</div>
            </div>
            <button type="button" onClick={onClose} className="text-white/80 hover:text-white"><X size={16} /></button>
          </div>
          <div className="p-5 space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label="Per instalment" value={fmtMoney(existing.amount_per_installment)} />
              <Stat label="Every" value={`${existing.interval_days}d`} />
              <Stat label="Instalments" value={`${paidCount}/${existing.installments_total}`} tone="info" />
            </div>
            {missedCount > 0 && (
              <div className="border-l-4 border-red-500 bg-red-50 text-red-700 px-3 py-2 text-xs flex items-center gap-2" data-testid="plan-missed-alert">
                <AlertCircle size={14} /> {missedCount} instalment(s) marked missed — reach out to the client.
              </div>
            )}
            <ol className="space-y-1.5">
              {(existing.schedule || []).map((s) => (
                <li key={s.id} className="flex items-center justify-between border px-3 py-2 text-xs" style={{ borderColor: "var(--ll-border)" }} data-testid={`plan-row-${s.id}`}>
                  <span className="font-mono-fin">{fmtDate(s.due_date)}</span>
                  <span className="font-mono-fin">{fmtMoney(s.amount)}</span>
                  <InstallmentPill status={s.status} />
                </li>
              ))}
            </ol>
            {existing.agreement_token && existing.status === "pending_agreement" && (
              <div className="border p-3 text-xs" style={{ borderColor: "var(--ll-border)" }}>
                <div className="font-bold mb-1">Awaiting client acceptance</div>
                <div className="text-[var(--ll-ink-soft)] mb-2">Share this link if the email didn't arrive:</div>
                <code className="text-[11px] break-all" data-testid="plan-agreement-link">
                  {window.location.origin}/pay-plan/{existing.agreement_token}
                </code>
              </div>
            )}
          </div>
          <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
            <button type="button" onClick={cancelExisting} className="text-xs text-red-600 hover:underline" data-testid="plan-cancel">
              Cancel plan
            </button>
            <button type="button" onClick={onClose} className="text-sm px-3 py-2 bg-[#0b5d9e] text-white hover:opacity-90">Done</button>
          </div>
        </div>
      </div>
    );
  }

  // New-plan form
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid="payment-plan-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border w-full max-w-xl max-h-[92vh] overflow-y-auto shadow-xl" style={{ borderColor: "var(--ll-ink)" }}>
        <div className="px-5 py-3 border-b bg-[#0b5d9e] text-white flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-75">New payment plan</div>
            <div className="font-display font-bold text-sm">Invoice {invoice.invoice_number} · {invoice.bill_to_name}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white" data-testid="payment-plan-close"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="Invoice total" value={fmtMoney(invoice.total)} />
            <Stat label="Already paid" value={fmtMoney(alreadyPaid)} tone={alreadyPaid > 0 ? "info" : undefined} />
            <Stat label="Remaining" value={fmtMoney(remaining)} tone="warn" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Per instalment" icon={<DollarSign size={11} />}>
              <input type="number" step="0.01" min="0" value={form.amount_per_installment} onChange={(e) => setForm({ ...form, amount_per_installment: e.target.value })} className="input font-mono-fin" data-testid="plan-amount" autoFocus />
            </Field>
            <Field label="Every N days" icon={<Repeat size={11} />}>
              <input type="number" min="1" max="90" value={form.interval_days} onChange={(e) => setForm({ ...form, interval_days: e.target.value })} className="input" data-testid="plan-interval" />
            </Field>
            <Field label="Instalments" icon={<FileText size={11} />}>
              <input type="number" min="1" max="24" value={form.installments_total} onChange={(e) => setForm({ ...form, installments_total: e.target.value })} className="input" data-testid="plan-count" />
            </Field>
          </div>

          <Field label="First due date" icon={<Calendar size={11} />}>
            <input type="date" value={form.first_due_date} onChange={(e) => setForm({ ...form, first_due_date: e.target.value })} className="input" data-testid="plan-first-due" />
          </Field>

          {/* Live schedule preview */}
          <div className="border" style={{ borderColor: "var(--ll-border)" }}>
            <div className="px-3 py-2 bg-[var(--ll-bg-card)] text-[10px] uppercase tracking-widest flex items-center justify-between">
              <span>Preview — {schedule.length} instalments</span>
              <span className="font-mono-fin normal-case tracking-normal">Total: {fmtMoney(scheduledTotal)}</span>
            </div>
            <ol className="divide-y max-h-40 overflow-y-auto" style={{ borderColor: "var(--ll-border)" }} data-testid="plan-preview">
              {schedule.map((r, i) => (
                <li key={i} className="flex items-center justify-between px-3 py-1.5 text-xs" style={{ borderColor: "var(--ll-border)" }}>
                  <span className="font-mono-fin">{fmtDate(r.due)}</span>
                  <span className="font-mono-fin">{fmtMoney(r.amount)}</span>
                </li>
              ))}
            </ol>
          </div>

          {overByTooMuch && (
            <div className="border-l-4 border-red-500 bg-red-50 text-red-700 px-3 py-2 text-xs flex items-center gap-2">
              <AlertCircle size={14} /> Scheduled {fmtMoney(scheduledTotal)} is well above remaining {fmtMoney(remaining)} — reduce the amount or count.
            </div>
          )}
          {underByABit && !overByTooMuch && (
            <div className="border-l-4 border-amber-500 bg-amber-50 text-amber-800 px-3 py-2 text-xs flex items-center gap-2">
              <AlertCircle size={14} /> Scheduled {fmtMoney(scheduledTotal)} is {fmtMoney(-diff)} short of the remaining {fmtMoney(remaining)}. Client will owe the difference after the last instalment.
            </div>
          )}

          {invoice.bill_to_email && (
            <label className="flex items-start gap-2 py-1 cursor-pointer" data-testid="plan-send-agreement-row">
              <input type="checkbox" checked={form.send_agreement_email} onChange={(e) => setForm({ ...form, send_agreement_email: e.target.checked })} className="mt-1" data-testid="plan-send-agreement" />
              <div className="text-xs">
                <div className="font-medium flex items-center gap-1"><Mail size={11} /> Email the agreement to {invoice.bill_to_email}</div>
                <div className="text-[11px] text-[var(--ll-ink-soft)]">
                  Client gets a one-tap "Accept this plan" link. Honors your Mail-routing preference.
                </div>
              </div>
            </label>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex items-center justify-end gap-2" style={{ borderColor: "var(--ll-border)" }}>
          <button type="button" onClick={onClose} className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)]" style={{ borderColor: "var(--ll-border)" }}>Cancel</button>
          <button type="submit" disabled={saving || overByTooMuch} className="text-sm px-3 py-2 bg-[#0b5d9e] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5" data-testid="plan-create">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Create plan
          </button>
        </div>

        <style>{`
          .input { width: 100%; border: 1px solid var(--ll-border); padding: 6px 10px; font-size: 13px; background: white; }
          .input:focus { outline: 2px solid #0b5d9e; outline-offset: -1px; }
        `}</style>
      </form>
    </div>
  );
}


function Field({ label, children, icon }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1 flex items-center gap-1">
        {icon}{label}
      </div>
      {children}
    </label>
  );
}

function Stat({ label, value, tone }) {
  const bg = tone === "warn" ? "#FEF3C7" : tone === "info" ? "#DBEAFE" : "#F3F4F6";
  const fg = tone === "warn" ? "#92400E" : tone === "info" ? "#1E40AF" : "#1F2937";
  return (
    <div className="border p-2" style={{ background: bg, borderColor: "transparent" }}>
      <div className="text-[9px] uppercase tracking-widest" style={{ color: fg, opacity: 0.75 }}>{label}</div>
      <div className="font-mono-fin text-sm font-semibold" style={{ color: fg }}>{value}</div>
    </div>
  );
}

function InstallmentPill({ status }) {
  const map = {
    pending: { bg: "#F3F4F6", fg: "#374151", label: "Pending" },
    paid:    { bg: "#DCFCE7", fg: "#166534", label: "Paid" },
    missed:  { bg: "#FEE2E2", fg: "#991B1B", label: "Missed" },
  };
  const m = map[status] || map.pending;
  return (
    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5" style={{ background: m.bg, color: m.fg }}>
      {m.label}
    </span>
  );
}
